import { createContext, useCallback, useContext } from 'react';

import { isObject, noop } from 'lodash';

import { useUserCompany } from 'helpers/useUserCompany';
import localStorage from 'redesign/helpers/localStorage';

export interface LocalStorageContext {
    getPersistedData: <Value = unknown>(key: string | string[]) => Value | undefined;
    updatePersistedData: (key: string | string[], dataToPersist: unknown) => void;
    setPersistedData: (key: string | string[], dataToPersist: unknown) => void;
    clearPersistedData: (key: string | string[]) => void;
    clearAllPersistedData: () => void;
}

export const localStorageContext = createContext<LocalStorageContext>({
    getPersistedData: () => undefined,
    updatePersistedData: noop,
    setPersistedData: noop,
    clearPersistedData: noop,
    clearAllPersistedData: noop,
});

export const useLocalStorageContext = () => useContext(localStorageContext);

export const LOCALSTORAGE_SERIALIZED_SEPARATOR = '_';

export const getSerializedKey = (key: string | string[]) => {
    if (Array.isArray(key)) {
        return key.sort().join(LOCALSTORAGE_SERIALIZED_SEPARATOR);
    }
    return key;
};

const LocalStorageManager = ({ children }: { children: JSX.Element }) => {
    /**
     * we need immediate initialization of local storage to avoid reloading components.
     * The company is initially undefined, which initializes the localStorage twice (once with the undefined and once with the correct value).
     * To avoid this, we persist the last value of the company and then reload it directly.
     */
    const persistedCompany = localStorage.get('current_company');
    const company = useUserCompany() || persistedCompany;
    localStorage.set('current_company', company);

    const getPersistedData = useCallback(
        <Value,>(key: string | string[]): Value | undefined => {
            const serializedKey = getSerializedKey(key);
            const storedData: Value = localStorage.get(company);
            return storedData ? storedData[serializedKey] : undefined;
        },
        [company]
    );

    const updatePersistedData = useCallback(
        (key: string | string[], dataToPersist: unknown) => {
            const serializedKey = getSerializedKey(key);
            const previousLocalStorage = localStorage.get(company) ?? {};
            const newPersistedData =
                isObject(previousLocalStorage[serializedKey]) && isObject(dataToPersist)
                    ? { ...previousLocalStorage[serializedKey], ...dataToPersist }
                    : dataToPersist;
            localStorage.set(company, {
                ...previousLocalStorage,
                [serializedKey]: newPersistedData,
            });
        },
        [company]
    );

    const setPersistedData = useCallback(
        (key: string | string[], dataToPersist: unknown) => {
            const serializedKey = getSerializedKey(key);
            const previousLocalStorage = localStorage.get(company) ?? {};
            localStorage.set(company, {
                ...previousLocalStorage,
                [serializedKey]: dataToPersist,
            });
        },
        [company]
    );

    const clearPersistedData = useCallback(
        (key: string | string[]) => {
            const serializedKey = getSerializedKey(key);
            const previousLocalStorage = localStorage.get(company) ?? {};
            localStorage.set(company, {
                ...previousLocalStorage,
                [serializedKey]: undefined,
            });
        },
        [company]
    );

    const clearAllPersistedData = useCallback(() => {
        localStorage.remove(company);
    }, [company]);

    return (
        <localStorageContext.Provider
            value={{
                updatePersistedData,
                setPersistedData,
                clearPersistedData,
                clearAllPersistedData,
                getPersistedData,
            }}
        >
            {children}
        </localStorageContext.Provider>
    );
};

export default LocalStorageManager;
